import { useCallback, useEffect, useMemo, useRef } from "react";
import IFinalizePOShipmentDTO from "@models/DTO/IFinalizePOShipmentDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import IBreadcrumbs from "@models/IBreadcrumbs";
import IOwTableCols from "@models/IOwTableCols";
import IPO_Shipment_Catalog_Item_MapUI from "@models/IPO_Shipment_Catalog_Item_MapUI";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { usePurchaseOrderShipmentData } from "@hooks/purchase_orders/usePurchaseOrderShipmentData";
import { usePurchaseOrderShipmentSocket } from "@hooks/purchase_orders/usePurchaseOrderShipmentSocket";
import { usePurchaseOrderShipmentQuantity } from "@hooks/purchase_orders/usePurchaseOrderShipmentQuantity";
import { usePurchaseOrderShipmentSerials } from "@hooks/purchase_orders/usePurchaseOrderShipmentSerials";
import POShipmentSerialEntry from "./POShipmentSerialEntry/POShipmentSerialEntry";
import SimpleConfirmModal from "@components/shared/SimpleConfirmModal/SimpleConfirmModal";
import { Link, useMatch } from "react-router-dom";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import { useAppContextNonNull } from "@contexts/AppContext";

export const PurchaseOrderShipmentDetailFn = () => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const match = useMatch("purchase_orders/process_shipment/:guid");

    const crumbs = useMemo<IBreadcrumbs[]>(() => {
        return [
            {
                title: "dashboard",
                url: "/dashboard",
            },
            {
                title: "purchase_orders",
                url: "/purchase_orders",
            },
            {
                title: "shipment_detail",
                url: "",
            },
        ];
    }, []);

    const {
        poShipment,
        setPoShipment,
        poShipmentItems,
        setPoShipmentItems,
        dataFetch,
        setDataFetch,
        initialData,
        setInitialData,
        isFormDirty,
        setIsFormDirty,
        getPOShipment,
        warehouses,
    } = usePurchaseOrderShipmentData({
        poShipmentGuid: match?.params.guid || "",
    });

    usePurchaseOrderShipmentSocket({
        setPoShipment,
        setPoShipmentItems,
        setDataFetch,
        setInitialData,
        setIsFormDirty,
    });

    const { getTotalOrdered, copyQtyToIncoming, handleIncomingChange, handleWarehouseChange, savePOShipmentItem } =
        usePurchaseOrderShipmentQuantity({
            poShipment,
            setPoShipmentItems,
            setIsFormDirty,
        });

    const {
        serialState,
        initializeSerials,
        getItemState,
        addPendingSerial,
        addBulkPendingSerials,
        removePendingSerial,
        submitSerials,
        flushPendingSerials,
        deleteSerial,
    } = usePurchaseOrderShipmentSerials({
        poShipmentGuid: match?.params.guid || "",
    });

    const hasInitializedSerials = useRef(false);

    useEffect(() => {
        if (!hasInitializedSerials.current && poShipmentItems) {
            initializeSerials(poShipmentItems);
            hasInitializedSerials.current = true;
        }
    }, [poShipmentItems, initializeSerials]);

    useEffect(() => {
        hasInitializedSerials.current = false;
    }, [match?.params.guid]);

    const commitFinalize = useCallback(() => {
        const payload: IFinalizePOShipmentDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            poShipmentGuid: match?.params.guid || "",
        };

        socket.emit(EndpointsEnum.PurchaseOrderFinalizePOShipment, payload);
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    }, [userModel.guid, userModel.token, supplierModel.guid, match?.params.guid, socket, uiContext]);

    const closeConfirmModal = useCallback(() => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    }, [uiContext]);

    // Entered serials only exist client-side until saved — persist them all
    // before finalizing, since finalize itself carries no serials and a
    // processed shipment can never be edited again.
    const flushAndFinalize = useCallback(async () => {
        const flushed = await flushPendingSerials(poShipmentItems || []);
        if (!flushed) {
            closeConfirmModal();
            return;
        }
        commitFinalize();
    }, [flushPendingSerials, poShipmentItems, commitFinalize, closeConfirmModal]);

    const finalizePOShipment = useCallback(() => {
        // Warehouse suppliers must assign a warehouse to every shipment detail record
        if (supplierModel.has_warehouses) {
            const itemsMissingWarehouse = poShipmentItems?.filter((item) => !item.warehouse_id) || [];

            if (itemsMissingWarehouse.length > 0) {
                uiContext.showNotification({
                    message: "Please select a warehouse for all items before finalizing",
                    level: NotificationLevelEnum.error,
                });
                return;
            }
        }

        // Check if any has_serials items are missing serials (pending valid
        // serials count — they are auto-saved before the finalize commits)
        const serialItems = poShipmentItems?.filter((item) => item.Catalog_Item?.has_serials === true) || [];
        const itemsMissingSerials = serialItems.filter((item) => {
            const itemSerialState = serialState[item.catalog_item_id];
            const savedCount = itemSerialState?.serials.length || 0;
            const pendingValidCount = itemSerialState?.pendingSerials.filter((p) => p.isValid === true).length || 0;
            return savedCount + pendingValidCount < item.qty_received;
        });

        if (itemsMissingSerials.length > 0) {
            const modalComponent = (
                <SimpleConfirmModal
                    message={"some_items_are_missing_serials_do_you_want_to_proceed"}
                    subMessage={"finalized_shipments_will_not_be_able_to_be_edited"}
                    subMessageClassName={"text-center"}
                    messageClassName={"text-center"}
                    confirmFn={flushAndFinalize}
                    cancelFn={closeConfirmModal}
                />
            );
            uiContext.setModalComponent(modalComponent);
            uiContext.showModal(true);
        } else {
            flushAndFinalize();
        }
    }, [poShipmentItems, serialState, flushAndFinalize, closeConfirmModal, uiContext, supplierModel.has_warehouses]);

    // Initial data fetch
    useEffect(() => {
        getPOShipment();
    }, [getPOShipment]);

    // Table rendering functions
    const renderActionsFn = () => {
        return <div></div>;
    };

    const renderItemFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        return <div>{row.Catalog_Item?.name}</div>;
    };

    const renderQtyFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        const totalOrdered = getTotalOrdered(row);
        const hideCopy = poShipment?.is_processed;

        return (
            <div>
                {totalOrdered}{" "}
                {!hideCopy && (
                    <span className={"text-primary ms-4"}>
                        <OwButton title={"copy"} rightIconClass={"fas fa-chevron-double-right"} fn={() => copyQtyToIncoming(row)} />
                    </span>
                )}
            </div>
        );
    };

    const renderIncomingQtyFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        if (!poShipment?.is_processed) {
            return (
                <div className={"w-10r d-flex"}>
                    <div className={"me-2"}>
                        <OwInputWrapper name={"qty_received"} fn={(e: any) => handleIncomingChange(e, row)} value={row.qty_received} />
                    </div>
                    <div>
                        <OwButton title={"save"} disabled={!row.isDirty} fn={() => savePOShipmentItem(row)} />
                    </div>
                </div>
            );
        }
        return <div>{row.qty_received}</div>;
    };

    const renderWarehouseFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        if (!poShipment?.is_processed) {
            return (
                <div className="d-flex">
                    <OwSelectOption
                        name={"warehouse_id"}
                        options={warehouses}
                        valueProp="id"
                        titleProp="name"
                        fn={(e: any) => handleWarehouseChange(e, row)}
                        selectedOption={row}
                        selectedOptionProp="warehouse_id"
                        isRequired={true}
                    />
                </div>
            );
        } else {
            return <div>{row.Warehouse?.name}</div>;
        }
    };

    const rowDetailViewFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        return row.Catalog_Item?.has_serials === true;
    };

    const detailViewComponent = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        const itemState = getItemState(row.catalog_item_id);
        return (
            <POShipmentSerialEntry
                row={row}
                serialState={itemState}
                isProcessed={poShipment?.is_processed || false}
                maxSerials={row.qty_received}
                onAddSerial={addPendingSerial}
                onBulkAddSerials={addBulkPendingSerials}
                onRemoveSerial={removePendingSerial}
                onDeleteSerial={deleteSerial}
                onSubmitSerials={submitSerials}
            />
        );
    };

    const columns: IOwTableCols[] = [
        {
            isActionsCol: true,
            title: "",
            valueProp: "",
            renderFn: renderActionsFn,
            renderFnEmitsRow: true,
            hasChevron: true,
            showChevronFn: (row: IPO_Shipment_Catalog_Item_MapUI) => row.Catalog_Item?.has_serials === true,
        },
        {
            title: "item",
            valueProp: "",
            renderFn: renderItemFn,
            renderFnEmitsRow: true,
        },
        {
            title: "qty_remaining",
            valueProp: "",
            renderFn: renderQtyFn,
            renderFnEmitsRow: true,
        },
    ];

    if (supplierModel.has_warehouses) {
        columns.push({
            title: "warehouse",
            valueProp: "",
            renderFn: renderWarehouseFn,
            renderFnEmitsRow: true,
        });
    }

    columns.push({
        title: "incoming_qty",
        valueProp: "",
        renderFn: renderIncomingQtyFn,
        renderFnEmitsRow: true,
    });

    return (
        <div className="PurchaseOrderShipmentDetailFn">
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"shipment_detail"} leftIconName={"fad fa-truck-ramp"} />
                    {!poShipment?.is_processed ? (
                        <div className={"ms-auto d-flex flex-column justify-content-end"}>
                            <div className={"text-end"}>
                                <OwButton title={"finalize"} fn={finalizePOShipment} disabled={isFormDirty} />
                            </div>
                            <div>
                                {isFormDirty ? <OwLabel title={"save_all_changes_to_finalize"} className={"mt-2 text-warning"} /> : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={"mt-2 mb-4"}>
                <OwLabel title={"purchase_order"} postText={":"} className={"me-2"} />
                <span className="me-1">{poShipment?.Vendor_Source?.name} -</span>
                {poShipment?.PO_Shipment_Purchase_Order_Maps?.map((mapItem) => {
                    return (
                        <span key={mapItem.guid}>
                            <Link to={`/purchase_orders/${mapItem?.Purchase_Order?.guid}`}>{mapItem?.Purchase_Order?.name}</Link>
                        </span>
                    );
                })}
            </div>

            <div>
                <OwTable
                    columns={columns}
                    dataSet={poShipmentItems || []}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={dataFetch}
                    initialData={initialData}
                    rowDetailViewFn={rowDetailViewFn}
                    detailViewComponent={detailViewComponent}
                    detailViewAlwaysVisible={true}
                />
            </div>
        </div>
    );
};
